import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Ministry } from '../../models/ministry/ministry';
import { School } from '../../models/school/school';
import { ministryService } from '../../services/ministry-service';
import { schoolService } from '../../services/school-service';
import { configurationService } from '../../services/configuration-service';
import { Language } from '../../constants/language';
import { ROUTES } from '../../constants/routes';
import { useConfiguration } from '../../hooks/useConfiguration';
import { Logo } from '../ui';

const QRLanding: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { language } = useConfiguration();
  const isRTL = language === Language.AR;

  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);

  // Language toggle function
  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    
    // Update i18n
    i18n.changeLanguage(newLang);
    
    // Update configuration service
    const currentConfig = configurationService.getConfiguration();
    configurationService.setConfiguration({
      ...currentConfig,
      language: newLang as Language
    });
    
    // Set document direction for RTL support
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  // Social media links from footer
  const socialLinks = [
    { 
      name: 'Facebook', 
      href: 'https://www.facebook.com/zoeegypt',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    },
    { 
      name: 'SoundCloud', 
      href: 'https://soundcloud.com/zoe-egypt',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7.2 2.2v10.7c-.6-.2-1.2-.3-1.8-.3-2 0-3.6 1.6-3.6 3.6s1.6 3.6 3.6 3.6c2 0 3.6-1.6 3.6-3.6V9.4l8.8-1.9v6.8c-.6-.2-1.2-.3-1.8-.3-2 0-3.6 1.6-3.6 3.6s1.6 3.6 3.6 3.6c2 0 3.6-1.6 3.6-3.6V0.8L7.2 2.2z"/>
        </svg>
      )
    },
    { 
      name: 'YouTube', 
      href: 'https://www.youtube.com/@ZoeForAllNations',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      )
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const lang = i18n.language === 'ar' ? Language.AR : Language.EN;
        
        // Fetch ministries (first 6)
        const ministryResult = await ministryService.getMinistries(lang, {}, undefined, 1, 6);
        setMinistries(ministryResult.data);

        // Fetch schools (first 6) 
        const schoolResult = await schoolService.getSchools(lang, {}, undefined, 1, 6);
        setSchools(schoolResult.data);
      } catch (error) {
        console.error('Error fetching QR landing data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [i18n.language]);

  if (loading) {
    return (
      <div className={`min-h-screen bg-white flex items-center justify-center ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-6">
            <Logo className="w-full h-full filter" alt="ZOE Logo" />
          </div>
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
            <p className="text-gray-800 text-sm">{t('ui.loading', 'Loading...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative px-6 py-16">
          <div className="text-center max-w-md mx-auto">
            {/* ZOE Logo - Larger size with scale 1.5 */}
            <div className="w-32 h-32 mx-auto mb-8">
              <Logo 
                className="w-full h-full mx-auto scale-150 lg:scale-[1.75] md:scale-[1.6] transition-opacity duration-300"
                alt="ZOE Logo" 
                style={{
                  filter: 'brightness(0) saturate(100%) invert(1) contrast(100%) drop-shadow(0 25px 50px rgba(0, 0, 0, 0.3))',
                  willChange: 'auto'
                }}
              />
            </div>
            
            {/* Tagline */}
            <h1 className="text-2xl font-bold mb-3 leading-tight">
              {t('pages.qr.tagline', 'ZOE for All Nations')}
            </h1>
            <p className="text-gray-300 text-sm leading-relaxed mb-8">
              {t('pages.qr.subtitle', 'Bringing Heaven to Earth by doing what Jesus did')}
            </p>
            
            {/* Primary CTA - Smaller and more professional */}
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-medium text-sm rounded-lg hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              {t('pages.qr.visit-website', 'Visit ZOE Website')}
            </Link>
          </div>
        </div>
      </div>

      {/* Language Switcher & Social Media Section */}
      <div className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            
            {/* Language Switcher */}
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
              <span className="text-sm font-medium text-gray-600">
                {t('footer.language', 'Language')}:
              </span>
              <button 
                onClick={toggleLanguage}
                className="px-4 py-2 bg-black text-white hover:bg-gray-800 font-medium text-sm transition-colors duration-300 rounded-lg shadow-md hover:shadow-lg transform hover:scale-105"
              >
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </button>
            </div>

            {/* Social Media Links */}
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
              <span className="text-sm font-medium text-gray-600 hidden sm:block">
                {t('general.social-media.follow-us-on', 'Follow us')}:
              </span>
              <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    className="w-10 h-10 bg-black hover:bg-gray-800 text-white flex items-center justify-center transition-all duration-300 group rounded-lg shadow-md hover:shadow-lg transform hover:scale-110"
                    aria-label={social.name}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="text-white transition-colors duration-300">
                      {social.icon}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="px-6 py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto space-y-12">
        
          {/* Ministries Section */}
          {ministries.length > 0 && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {t('pages.qr.our-ministries', 'Our Ministries')}
                </h2>
                <div className="w-16 h-0.5 bg-black mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ministries.slice(0, 6).map((ministry) => (
                  <MinistryCard key={ministry.id} ministry={ministry} isRTL={isRTL} />
                ))}
              </div>
            </div>
          )}

          {/* Schools Section */}
          {schools.length > 0 && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {t('pages.qr.our-schools', 'Our Schools')}
                </h2>
                <div className="w-16 h-0.5 bg-black mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {schools.slice(0, 6).map((school) => (
                  <SchoolCard key={school.id} school={school} isRTL={isRTL} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Space */}
      <div className="bg-white h-12"></div>
    </div>
  );
};

// Ministry Card Component
interface MinistryCardProps {
  ministry: Ministry;
  isRTL: boolean;
}

const MinistryCard: React.FC<MinistryCardProps> = ({ ministry, isRTL }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const handleClick = () => {
    // Navigate to ministry detail page
    navigate(`${ROUTES.MINISTRY_DETAIL.replace(':ministryId', ministry.id.toString())}`);
  };

  const handleSocialClick = (e: React.MouseEvent, destination: string) => {
    e.stopPropagation();
    window.open(destination, '_blank', 'noopener,noreferrer');
  };

  return (
    <div 
      onClick={handleClick}
      className={`group bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer hover:border-gray-300 transform hover:scale-105 ${
        isRTL ? 'text-right' : 'text-left'
      }`}
    >
      {/* Ministry Header */}
      <div className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-4 mb-4`}>
        {/* Logo */}
        <div className="w-14 h-14 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gray-100 transition-colors">
          {ministry.media?.logo?.url ? (
            <img
              src={ministry.media.logo.url}
              alt={ministry.title}
              className="w-10 h-10 object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `<div class="w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center text-sm font-bold">${ministry.title.charAt(0)}</div>`;
                }
              }}
            />
          ) : (
            <div className="w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center text-sm font-bold">
              {ministry.title.charAt(0)}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-2 group-hover:text-black transition-colors">
            {ministry.title}
          </h3>
          <p className="text-gray-600 text-xs leading-relaxed line-clamp-3">
            {ministry.description}
          </p>
        </div>
      </div>

      {/* Enhanced Social Media Icons */}
      {(ministry.socialMedia?.facebook || ministry.socialMedia?.instagram || ministry.socialMedia?.youtube || ministry.socialMedia?.whatsapp) && (
        <div className={`flex ${isRTL ? 'space-x-reverse justify-end' : ''} space-x-2 pt-3 border-t border-gray-100`}>
          <span className="text-xs text-gray-500 font-medium flex items-center">
            {t('general.social-media.follow-us-on', 'Follow:')} 
          </span>
          <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
          {ministry.socialMedia?.facebook && (
            <button
              onClick={(e) => handleSocialClick(e, ministry.socialMedia!.facebook!)}
              className="w-7 h-7 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-sm hover:shadow-md"
              aria-label="Facebook"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </button>
          )}
          {ministry.socialMedia?.instagram && (
            <button
              onClick={(e) => handleSocialClick(e, ministry.socialMedia!.instagram!)}
              className="w-7 h-7 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-sm hover:shadow-md"
              aria-label="Instagram"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </button>
          )}
          {ministry.socialMedia?.youtube && (
            <button
              onClick={(e) => handleSocialClick(e, ministry.socialMedia!.youtube!)}
              className="w-7 h-7 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-sm hover:shadow-md"
              aria-label="YouTube"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </button>
          )}
          {ministry.socialMedia?.whatsapp && (
            <button
              onClick={(e) => handleSocialClick(e, ministry.socialMedia!.whatsapp!)}
              className="w-7 h-7 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-sm hover:shadow-md"
              aria-label="WhatsApp"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.89 3.488"/>
              </svg>
            </button>
          )}
          </div>
        </div>
      )}
    </div>
  );
};

// School Card Component
interface SchoolCardProps {
  school: School;
  isRTL: boolean;
}

const SchoolCard: React.FC<SchoolCardProps> = ({ school, isRTL }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    // Navigate to school detail page using React Router
    navigate(`${ROUTES.SCHOOL_DETAIL.replace(':schoolKey', school.key).replace(':schoolId', school.id.toString())}`);
  };

  return (
    <div 
      onClick={handleClick}
      className={`group bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer hover:border-gray-300 transform hover:scale-105 ${
        isRTL ? 'text-right' : 'text-left'
      }`}
    >
      {/* School Header */}
      <div className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-4 mb-4`}>
        {/* Logo */}
        <div className="w-14 h-14 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gray-100 transition-colors">
          {school.media?.logo?.url ? (
            <img
              src={school.media.logo.url}
              alt={school.title}
              className="w-10 h-10 object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `<div class="w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center text-sm font-bold">${school.title.charAt(0)}</div>`;
                }
              }}
            />
          ) : (
            <div className="w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center text-sm font-bold">
              {school.title.charAt(0)}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-2 group-hover:text-black transition-colors">
            {school.title}
          </h3>
          <p className="text-gray-600 text-xs leading-relaxed line-clamp-3 mb-3">
            {school.shortDescription || school.description}
          </p>
        </div>
      </div>

      {/* Duration & Status */}
      <div className={`flex items-center ${isRTL ? 'space-x-reverse justify-end' : ''} space-x-2 text-xs pt-3 border-t border-gray-100`}>
        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
          {school.duration}
        </span>
      </div>
    </div>
  );
};

export default QRLanding;